import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBagIcon, 
  SparklesIcon,
  CubeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { Product } from '../types';
import { ApiService } from '../services/api';

const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [recentProducts, setRecentProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHomeData();
  }, []);

  const loadHomeData = async () => {
    try {
      setIsLoading(true);
      
      // 병렬로 데이터 로드
      const [featuredResponse, recentResponse] = await Promise.all([
        ApiService.getFeaturedProducts(8),
        ApiService.getRecentProducts(8)
      ]);

      if (featuredResponse.success && featuredResponse.data) {
        setFeaturedProducts(featuredResponse.data);
      }

      if (recentResponse.success && recentResponse.data) {
        setRecentProducts(recentResponse.data);
      }
    } catch (error) {
      console.error('Failed to load home data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: CubeIcon,
      title: 'Point 결제 시스템',
      description: 'Cash Point와 Gold Point를 활용한 특별한 결제 경험'
    },
    {
      icon: SparklesIcon,
      title: 'Stage별 할인 혜택',
      description: '멤버십 등급에 따른 차등 할인 혜택 제공'
    },
    {
      icon: ShoppingBagIcon,
      title: '다양한 상품',
      description: '관리자 등록 상품부터 자동 크롤링 상품까지'
    },
    {
      icon: TruckIcon,
      title: '안전한 배송',
      description: '신뢰할 수 있는 배송 시스템으로 안전하게 전달'
    }
  ];

  const formatPrice = (cashPrice: number, goldPrice: number) => {
    const parts = [];
    if (cashPrice > 0) parts.push(`${cashPrice.toLocaleString()} CP`);
    if (goldPrice > 0) parts.push(`${goldPrice.toLocaleString()} GP`);
    return parts.join(' + ') || '무료';
  };

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'cash_only':
        return <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Cash Only</span>;
      case 'gold_only':
        return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">Gold Only</span>;
      case 'mixed':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Mixed</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-kahan-primary to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              KAHAN Mall
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              MLM 회원만을 위한 특별한 쇼핑몰
            </p>
            <p className="text-lg mb-12 text-blue-200 max-w-2xl mx-auto">
              Cash Point와 Gold Point를 활용하여 더 저렴하게, 더 특별하게 쇼핑하세요. 
              멤버십 등급에 따른 특별 할인 혜택도 놓치지 마세요!
            </p>
            <div className="space-x-4">
              <Link
                to="/products"
                className="bg-white text-kahan-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                전체 상품 보기
              </Link>
              <Link
                to="/register"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-kahan-primary transition-colors inline-block"
              >
                회원가입
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              KAHAN Mall의 특별함
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              일반 쇼핑몰과는 다른, MLM 회원만을 위한 특별한 혜택들을 만나보세요
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-kahan-primary text-white rounded-lg mb-4">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">추천 상품</h2>
              <p className="text-gray-600">KAHAN Mall이 특별히 추천하는 상품들</p>
            </div>
            <Link
              to="/products"
              className="text-kahan-primary font-medium hover:text-blue-700 transition-colors"
            >
              전체보기 →
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
                  <div className="bg-gray-300 h-48 rounded mb-4"></div>
                  <div className="bg-gray-300 h-4 rounded mb-2"></div>
                  <div className="bg-gray-300 h-4 rounded w-2/3 mb-2"></div>
                  <div className="bg-gray-300 h-4 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group"
                >
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                        <CubeIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {product.category}
                      </span>
                      {getPaymentTypeBadge(product.paymentType)}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-sm font-semibold text-kahan-primary">
                      {formatPrice(product.cashPointPrice, product.goldPointPrice)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Recent Products Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">최신 상품</h2>
              <p className="text-gray-600">새롭게 등록된 따끈따끈한 상품들</p>
            </div>
            <Link
              to="/products?sort=newest"
              className="text-kahan-primary font-medium hover:text-blue-700 transition-colors"
            >
              전체보기 →
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="bg-gray-100 rounded-lg shadow-sm p-4 animate-pulse">
                  <div className="bg-gray-300 h-48 rounded mb-4"></div>
                  <div className="bg-gray-300 h-4 rounded mb-2"></div>
                  <div className="bg-gray-300 h-4 rounded w-2/3 mb-2"></div>
                  <div className="bg-gray-300 h-4 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="bg-gray-50 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group"
                >
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                        <CubeIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                        {product.category}
                      </span>
                      {getPaymentTypeBadge(product.paymentType)}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-sm font-semibold text-kahan-primary">
                      {formatPrice(product.cashPointPrice, product.goldPointPrice)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-kahan-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            지금 바로 시작해보세요
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            KAHAN MLM 회원만의 특별한 쇼핑 경험을 놓치지 마세요
          </p>
          <Link
            to="/register"
            className="bg-white text-kahan-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors inline-block"
          >
            회원가입하고 혜택 받기
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;